/**
 * API client for the Influencer Portal
 * 
 * Includes Turnstile verification for login endpoints
 */

// In production, use the deployed Worker URL; in dev, use the local proxy
export const API_BASE = import.meta.env.PROD 
  ? 'https://creatorhub360-portal-api.crat3r.workers.dev/api'
  : '/api';

/**
 * Route an external product image URL through our server-side proxy.
 * This bypasses Alibaba CDN hotlink protection which blocks direct browser requests.
 * The proxy fetches server-side with a valid Referer header and caches for 24h.
 */
export function proxyImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  return `${API_BASE}/img-proxy?url=${encodeURIComponent(url)}`;
}

interface ApiError {
  error: string;
  message?: string;
}

/**
 * Thrown when the server responds 401 (unauthenticated). Consumers like
 * AuthContext can catch this and redirect to /login.
 */
export class UnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

// Endpoints that legitimately return 401 as part of the normal login flow.
// Emitting our UnauthorizedError for these would trigger a redirect loop
// away from the login screen itself — and crucially, the landing page would
// bounce signed-out visitors to /login because the initial /auth/me probe
// returns 401 for guests.
const AUTH_FLOW_PATHS = [
  '/auth/me',
  '/auth/login',
  '/auth/admin/verify-code',
  '/auth/accept-invite',
  '/auth/vendor-accept-invite',
  '/auth/set-password',
  '/auth/reset-password',
  '/auth/signup',
  '/auth/signup/verify',
  '/auth/change-password',
];

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(fn: UnauthorizedListener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

function emitUnauthorized() {
  for (const fn of unauthorizedListeners) {
    try { fn(); } catch { /* ignore listener errors */ }
  }
}

class ApiClient {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errMsg = (data as ApiError).error || 'Request failed';
      if (response.status === 401 && !AUTH_FLOW_PATHS.some((p) => endpoint.startsWith(p))) {
        emitUnauthorized();
        throw new UnauthorizedError(errMsg);
      }
      throw new Error(errMsg);
    }

    return data as T;
  }

  // ============================================================
  // AUTH ENDPOINTS
  // ============================================================

  /**
   * Login with email, password, and Turnstile token
   * For admin: returns requiresVerification = true if 2FA needed
   * For influencer: returns user directly
   */
  async login(email: string, password: string, turnstileToken: string) {
    return this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, turnstileToken }),
    });
  }

  /**
   * Verify admin 2FA code
   */
  async verifyAdminCode(email: string, code: string, turnstileToken?: string) {
    return this.request<{ user: User }>('/auth/admin/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email, code, turnstileToken }),
    });
  }

  async logout() {
    return this.request<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    });
  }

  async getMe() {
    return this.request<MeResponse>('/auth/me');
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ success: boolean }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async setPassword(token: string, password: string, turnstileToken?: string) {
    return this.request<{ success: boolean; user: User }>('/auth/set-password', {
      method: 'POST',
      body: JSON.stringify({ token, password, turnstileToken }),
    });
  }

  async resetPassword(token: string, password: string, turnstileToken?: string) {
    return this.request<{ success: boolean }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password, turnstileToken }),
    });
  }

  /**
   * Request a password reset email. Always returns a generic success message
   * to avoid email-enumeration attacks.
   */
  async requestPasswordReset(email: string, turnstileToken?: string) {
    return this.request<{ data: { message: string } }>('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email, turnstileToken }),
    });
  }

  /**
   * Accept invite and set password (with Turnstile)
   */
  async acceptInvite(
    token: string,
    password: string,
    confirmPassword: string,
    turnstileToken: string
  ) {
    return this.request<{ success: boolean; user: User }>('/auth/accept-invite', {
      method: 'POST',
      body: JSON.stringify({ token, password, confirmPassword, turnstileToken }),
    });
  }

  async signup(data: {
    role: 'creator' | 'vendor';
    email: string;
    password: string;
    name: string;
    company_name?: string;
    turnstileToken?: string;
  }) {
    return this.request<{ data: { requiresVerification: boolean } }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async signupVerify(data: { email: string; code: string }) {
    return this.request<{ data: { requiresVerification: boolean; user?: User } }>('/auth/signup/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============================================================
  // ADMIN - INFLUENCER MANAGEMENT
  // ============================================================

  async getInfluencers() {
    return this.request<{ influencers: InfluencerWithStatus[] }>('/admin/influencers');
  }

  async getInfluencer(id: number) {
    return this.request<{ influencer: Influencer; user: InfluencerUser | null }>(
      `/admin/influencers/${id}`
    );
  }

  async createInfluencer(data: CreateInfluencerData) {
    return this.request<{ influencer: Influencer }>('/admin/influencers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInfluencer(id: number, data: UpdateInfluencerData) {
    return this.request<{ influencer: Influencer }>(`/admin/influencers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteInfluencer(id: number) {
    return this.request<{ success: boolean; message: string }>(`/admin/influencers/${id}`, {
      method: 'DELETE',
    });
  }

  async createInfluencerLogin(id: number, email: string) {
    return this.request<{ user: { id: number; email: string }; inviteUrl: string; expiresAt: string }>(
      `/admin/influencers/${id}/create-login`,
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  async resetInfluencerPassword(id: number) {
    return this.request<{ success: boolean; message: string; resetUrl?: string }>(
      `/admin/influencers/${id}/reset-password`,
      { method: 'POST' }
    );
  }

  /**
   * Send invite email to influencer
   */
  async sendInfluencerInvite(id: number, email?: string) {
    return this.request<SendInviteResponse>(
      `/admin/influencers/${id}/send-invite`,
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  /**
   * Update influencer's user email
   */
  async updateInfluencerEmail(id: number, email: string) {
    return this.request<{ success: boolean; message: string; email: string }>(
      `/admin/influencers/${id}/update-email`,
      {
        method: 'PATCH',
        body: JSON.stringify({ email }),
      }
    );
  }

  // Legacy: getStorefronts (maps to influencers)
  async getStorefronts() {
    return this.request<{ storefronts: StorefrontWithStats[] }>('/admin/storefronts');
  }

  // ============================================================
  // ADMIN - USER MANAGEMENT (super_admin)
  // ============================================================

  async getAdminUsers() {
    return this.request<{ users: AdminUser[] }>('/admin/users');
  }

  async inviteAdminUser(data: { email: string; name?: string; admin_level?: string }) {
    return this.request<{
      user: AdminUser;
      invite_url: string;
      email_sent: boolean;
      email_error?: string;
      expires_at: string;
    }>('/admin/users/invite', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async resendAdminInvite(userId: number) {
    return this.request<{
      invite_url: string;
      email_sent: boolean;
      email_error?: string;
      expires_at: string;
    }>(`/admin/users/${userId}/resend-invite`, {
      method: 'POST',
    });
  }

  async updateAdminUser(userId: number, data: { admin_level?: string; name?: string }) {
    return this.request<{ user: AdminUser }>(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminUser(userId: number) {
    return this.request<{ success: boolean; message: string }>(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================
  // ADMIN - ORDERS REVIEW
  // ============================================================

  async getOrdersForReview(limit = 50, cursor?: number) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', String(cursor));
    return this.request<{ orders: OrderFact[]; nextCursor: number | null }>(
      `/admin/orders/review?${params.toString()}`
    );
  }

  // ============================================================
  // INFLUENCER ENDPOINTS
  // ============================================================

  async getInfluencerProfile() {
    return this.request<InfluencerProfile>('/influencer/profile');
  }

  async getInfluencerOrders(params: { range?: MetricsRange; limit?: number; cursor?: number }) {
    const searchParams = new URLSearchParams();
    if (params.range) searchParams.set('range', params.range);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.cursor) searchParams.set('cursor', String(params.cursor));

    return this.request<{ orders: OrderFact[]; nextCursor: number | null }>(
      `/influencer/orders?${searchParams.toString()}`
    );
  }

  async getInfluencerMetrics(range: MetricsRange = '30d') {
    return this.request<MetricsSummary>(`/influencer/metrics/summary?range=${range}`);
  }

  async getInfluencerMetricsByState(range: MetricsRange = '30d') {
    return this.request<{ states: StateMetrics[]; range: string }>(
      `/influencer/metrics/by-state?range=${range}`
    );
  }

  /**
   * Get single order detail for influencer
   */
  async getInfluencerOrderDetail(shopifyOrderId: string) {
    return this.request<{ order: OrderDetail }>(
      `/influencer/orders/${encodeURIComponent(shopifyOrderId)}`
    );
  }

  // ============================================================
  // ADMIN - VIEW AS INFLUENCER ENDPOINTS
  // ============================================================

  /**
   * Get orders for a specific influencer (admin view)
   */
  async getAdminInfluencerOrders(
    influencerId: number,
    params: { range?: MetricsRange; limit?: number; cursor?: number }
  ) {
    const searchParams = new URLSearchParams();
    if (params.range) searchParams.set('range', params.range);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.cursor) searchParams.set('cursor', String(params.cursor));

    return this.request<{ orders: OrderFact[]; nextCursor: number | null }>(
      `/admin/influencers/${influencerId}/orders?${searchParams.toString()}`
    );
  }

  /**
   * Get single order detail for a specific influencer (admin view)
   */
  async getAdminInfluencerOrderDetail(influencerId: number, shopifyOrderId: string) {
    return this.request<{ order: OrderDetail }>(
      `/admin/influencers/${influencerId}/orders/${encodeURIComponent(shopifyOrderId)}`
    );
  }

  /**
   * Get metrics summary for a specific influencer (admin view)
   */
  async getAdminInfluencerMetrics(influencerId: number, range: MetricsRange = '30d') {
    return this.request<MetricsSummary>(
      `/admin/influencers/${influencerId}/metrics/summary?range=${range}`
    );
  }

  /**
   * Get orders by state for a specific influencer (admin view)
   */
  async getAdminInfluencerMetricsByState(influencerId: number, range: MetricsRange = '30d') {
    return this.request<{ states: StateMetrics[]; range: string }>(
      `/admin/influencers/${influencerId}/metrics/by-state?range=${range}`
    );
  }

  // ============================================================
  // ADMIN - VENDORS
  // ============================================================

  async getVendors() {
    return this.request<{ vendors: VendorWithStatus[] }>('/admin/vendors');
  }

  async getVendor(id: number) {
    return this.request<{ vendor: Vendor }>(`/admin/vendors/${id}`);
  }

  async createVendor(data: CreateVendorData) {
    return this.request<{ vendor: VendorWithStatus; invite_sent: boolean; invite_url?: string; invite_error?: string }>('/admin/vendors', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVendor(id: number, data: UpdateVendorData) {
    return this.request<{ vendor: Vendor }>(`/admin/vendors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ============================================================
  // ADMIN - CATALOG PRODUCTS
  // ============================================================

  async getCatalogProducts(filters?: CatalogProductFilters) {
    const params = new URLSearchParams();
    if (filters?.vendor_id_fk) params.set('vendor_id_fk', String(filters.vendor_id_fk));
    if (filters?.category) params.set('category', filters.category);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.customizable !== undefined) params.set('customizable', String(filters.customizable));
    if (filters?.printing_available !== undefined) params.set('printing_available', String(filters.printing_available));
    if (filters?.embroidery_available !== undefined) params.set('embroidery_available', String(filters.embroidery_available));
    
    return this.request<{ products: CatalogProductWithVendor[] }>(
      `/admin/catalog-products?${params.toString()}`
    );
  }

  async getCatalogProduct(id: number) {
    return this.request<{ product: CatalogProductWithImages }>(
      `/admin/catalog-products/${id}`
    );
  }

  async createCatalogProduct(data: CreateCatalogProductData) {
    return this.request<{ product: CatalogProduct }>('/admin/catalog-products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCatalogProduct(id: number, data: UpdateCatalogProductData) {
    return this.request<{ product: CatalogProduct }>(`/admin/catalog-products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async addCatalogProductImage(productId: number, data: { image_url: string; alt_text?: string; sort_order?: number }) {
    return this.request<{ image: CatalogImage }>(`/admin/catalog-products/${productId}/images`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCatalogProductImage(productId: number, imageId: number) {
    return this.request<{ success: boolean }>(`/admin/catalog-products/${productId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================
  // ADMIN - PRODUCT REQUESTS
  // ============================================================

  async getProductRequests(status?: ProductRequestStatus) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    return this.request<{ requests: ProductRequestWithDetails[] }>(
      `/admin/product-requests?${params.toString()}`
    );
  }

  async getProductRequest(id: number) {
    return this.request<{ request: ProductRequestWithDetails }>(
      `/admin/product-requests/${id}`
    );
  }

  async updateProductRequestStatus(id: number, status: ProductRequestStatus, adminNotes?: string) {
    return this.request<{ request: ProductRequest }>(`/admin/product-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, admin_notes: adminNotes }),
    });
  }

  // ============================================================
  // ADMIN - SHOPIFY PRODUCTS
  // ============================================================

  async getUnlinkedShopifyProducts() {
    return this.request<{ products: ShopifyProduct[] }>('/admin/shopify-products/unlinked');
  }

  async manualLinkShopifyProduct(data: { shopify_product_id: string; influencer_id_fk?: number; vendor_catalog_product_id_fk?: number }) {
    return this.request<{ product: ShopifyProduct }>('/admin/shopify-products/manual-link', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============================================================
  // ADMIN - ANALYTICS
  // ============================================================

  async getTopShopifyProducts(range: MetricsRange = '30d', limit = 20) {
    return this.request<{ products: TopShopifyProduct[]; range: string }>(
      `/admin/analytics/top-shopify-products?range=${range}&limit=${limit}`
    );
  }

  async getTopVendorCatalogProducts(range: MetricsRange = '30d', limit = 20) {
    return this.request<{ products: TopCatalogProduct[]; range: string }>(
      `/admin/analytics/top-vendor-catalog-products?range=${range}&limit=${limit}`
    );
  }

  async getTopInfluencers(range: MetricsRange = '30d', limit = 20) {
    return this.request<{ influencers: TopInfluencer[]; range: string }>(
      `/admin/analytics/top-influencers?range=${range}&limit=${limit}`
    );
  }

  async getDashboardAnalytics(range: MetricsRange = '30d') {
    return this.request<{
      summary: {
        totalRevenue: number;
        totalOrders: number;
        totalInfluencers: number;
        activeInfluencers: number;
        avgOrderValue: number;
        totalVendors: number;
      };
      revenueByDay: Array<{ date: string; revenue: number; orders: number }>;
      websitePipeline: Record<string, number>;
      productRequests: Record<string, number>;
      topInfluencers: Array<{ influencer_id: number; name: string; total_orders: number; total_revenue: number }>;
      range: string;
    }>(`/admin/analytics/dashboard?range=${range}`);
  }

  // ============================================================
  // INFLUENCER - CATALOG BROWSING
  // ============================================================

  async browseCatalogProducts(filters?: InfluencerCatalogFilters) {
    const params = new URLSearchParams();
    if (filters?.vendor_id_fk) params.set('vendor_id_fk', String(filters.vendor_id_fk));
    if (filters?.category) params.set('category', filters.category);
    if (filters?.customizable !== undefined) params.set('customizable', String(filters.customizable));
    if (filters?.printing_available !== undefined) params.set('printing_available', String(filters.printing_available));
    if (filters?.embroidery_available !== undefined) params.set('embroidery_available', String(filters.embroidery_available));
    
    return this.request<{ products: InfluencerCatalogProduct[] }>(
      `/influencer/catalog-products?${params.toString()}`
    );
  }

  async getCatalogProductDetail(id: number) {
    return this.request<{ product: InfluencerCatalogProductDetail }>(
      `/influencer/catalog-products/${id}`
    );
  }

  async getIntelProducts(params: { search?: string; vertical?: string; category?: string; limit?: number; offset?: number } = {}) {
    const p = new URLSearchParams();
    if (params.search)   p.set('search',   params.search);
    if (params.vertical) p.set('vertical', params.vertical);
    if (params.category) p.set('category', params.category);
    if (params.limit)    p.set('limit',    String(params.limit));
    if (params.offset)   p.set('offset',   String(params.offset));
    return this.request<{
      products: Array<{
        id: number; title: string; short_description: string | null;
        primary_image_url: string | null; price_min: number; price_max: number;
        currency: string; vertical: string; category: string;
        subcategory: string | null; tags: string[];
      }>;
      total: number; limit: number; offset: number;
    }>(`/influencer/intel-products?${p.toString()}`);
  }

  async getIntelProductVerticals() {
    return this.request<{ verticals: string[] }>('/influencer/intel-products/verticals');
  }

  // ============================================================
  // INFLUENCER - PRODUCT REQUESTS
  // ============================================================

  async createProductRequest(data: CreateProductRequestData) {
    return this.request<{ request: ProductRequest }>('/influencer/product-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyProductRequests() {
    return this.request<{ requests: ProductRequestWithDetails[] }>('/influencer/product-requests');
  }

  // ============================================================
  // INFLUENCER - LINKED SHOPIFY PRODUCTS
  // ============================================================

  async getMyShopifyProducts() {
    return this.request<{ products: ShopifyProduct[] }>('/influencer/shopify-products');
  }

  async getProductMetrics(range: MetricsRange = '30d') {
    return this.request<{ products: ProductMetric[]; range: string }>(
      `/influencer/metrics/products?range=${range}`
    );
  }

  // ============================================================
  // ADMIN - IMAGE UPLOADS (R2)
  // ============================================================

  async presignCatalogImage(data: {
    vendor_catalog_product_id: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
  }) {
    return this.request<PresignUploadResponse>('/admin/uploads/catalog-image/presign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadCatalogImage(uploadUrl: string, file: File): Promise<{ success: boolean; objectKey: string }> {
    // The uploadUrl is relative to our API
    const response = await fetch(`${API_BASE}${uploadUrl.replace('/api', '')}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  async completeCatalogImageUpload(data: {
    vendor_catalog_product_db_id: number;
    publicUrl: string;
    objectKey: string;
    altText?: string;
    sortOrder?: number;
  }) {
    return this.request<{ success: boolean; image: CatalogImage }>('/admin/uploads/catalog-image/complete', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteCatalogImage(imageId: number) {
    return this.request<{ success: boolean }>(`/admin/uploads/catalog-image/${imageId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================
  // ADMIN - WEBSITES MANAGEMENT
  // ============================================================

  async getWebsites(status?: SiteStatus) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    return this.request<{ websites: AdminWebsiteListItem[] }>(
      `/admin/websites${params.toString() ? `?${params.toString()}` : ''}`
    );
  }

  async getWebsiteDetail(influencerDbId: number) {
    return this.request<AdminWebsiteDetail>(`/admin/websites/${influencerDbId}`);
  }

  async updateWebsite(influencerDbId: number, data: { status?: SiteStatus; site_domain?: string | null }) {
    return this.request<{ success: boolean; site: InfluencerSite }>(`/admin/websites/${influencerDbId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateWebsiteIntake(influencerDbId: number, data: SiteIntakeData) {
    return this.request<{ success: boolean; intake: InfluencerSiteIntake }>(`/admin/websites/${influencerDbId}/intake`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async generateDraft(influencerDbId: number, credentials: {
    shopify_storefront_api_token: string;
    shopify_storefront_id: string;
    private_storefront_api_token: string;
    public_customer_account_api_client_id: string;
    public_customer_account_api_url: string;
    shop_id: string;
    force?: boolean;
  }) {
    return this.request<{
      status: string;
      worker_name: string;
      staging_url: string;
      message: string;
    }>(`/admin/websites/${influencerDbId}/generate-draft`, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async parseShopifyScreenshot(image: string, mediaType: string) {
    return this.request<{
      credentials: {
        shopify_storefront_api_token: string;
        shopify_storefront_id: string;
        private_storefront_api_token: string;
        public_customer_account_api_client_id: string;
        public_customer_account_api_url: string;
        shop_id: string;
      };
      detected_count: number;
    }>('/admin/websites/parse-shopify-screenshot', {
      method: 'POST',
      body: JSON.stringify({ image, mediaType }),
    });
  }

  async resetGeneration(influencerDbId: number) {
    return this.request<{ ok: boolean; status: string }>(
      `/admin/websites/${influencerDbId}/reset-generation`,
      { method: 'POST' },
    );
  }

  async retryBuild(influencerDbId: number) {
    return this.request<{ ok: boolean; commit_sha: string }>(
      `/admin/websites/${influencerDbId}/retry-build`,
      { method: 'POST' },
    );
  }

  async getBuildStatus(influencerDbId: number) {
    return this.request<{
      ready: boolean;
      status: string;
      conclusion: string | null;
      run_url: string | null;
      staging_url: string | null;
      site_status: SiteStatus;
    }>(`/admin/websites/${influencerDbId}/build-status`);
  }

  async getFile(influencerDbId: number, filePath: string) {
    return this.request<{ path: string; content: string; sha: string }>(
      `/admin/websites/${influencerDbId}/file?path=${encodeURIComponent(filePath)}`
    );
  }

  async listFiles(influencerDbId: number, dirPath: string = '') {
    return this.request<{
      entries: Array<{ path: string; type: 'file' | 'dir'; sha: string }>;
    }>(`/admin/websites/${influencerDbId}/files?path=${encodeURIComponent(dirPath)}`);
  }

  async saveFile(influencerDbId: number, data: {
    path: string;
    content: string;
    sha: string;
    message?: string;
  }) {
    return this.request<{ success: boolean }>(`/admin/websites/${influencerDbId}/file`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async commitFiles(influencerDbId: number, data: {
    files: Array<{ path: string; content: string }>;
    message?: string;
  }) {
    return this.request<{ success: boolean; committed: number }>(
      `/admin/websites/${influencerDbId}/commit-files`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  async aiEditFile(influencerDbId: number, data: {
    path: string;
    instruction: string;
  }) {
    return this.request<{
      path: string;
      original: string;
      suggested: string;
      sha: string;
    }>(`/admin/websites/${influencerDbId}/ai-edit`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async aiEditMultiFile(influencerDbId: number, data: { instruction: string }) {
    return this.request<{
      changes: Array<{ path: string; original: string; suggested: string }>;
      totalFiles: number;
    }>(`/admin/websites/${influencerDbId}/ai-edit-multi`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async scrapeInspiration(influencerDbId: number, url: string) {
    return this.request<{
      ok: boolean;
      url: string;
      extractedColors: string[];
      extractedFonts: string[];
      googleFontsLinks: string[];
      layoutSections: number;
      navStyle: string;
      screenshot: string;  // base64 PNG thumbnail
      scrapedAt: string;
    }>(`/admin/websites/${influencerDbId}/scrape-inspiration`, {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  // ============================================================
  // INFLUENCER - SITE (YOUR SITE)
  // ============================================================

  async getMySite() {
    return this.request<SiteDataResponse>('/influencer/site');
  }

  async submitSiteIntake(data: SiteIntakeData) {
    return this.request<{ success: boolean; status: SiteStatus; intake: InfluencerSiteIntake }>(
      '/influencer/site/intake',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  async getMySiteAssets() {
    return this.request<{ assets: InfluencerSiteAsset[] }>('/influencer/site/assets');
  }

  // ============================================================
  // INFLUENCER - SITE ASSET UPLOADS (R2)
  // ============================================================

  async presignSiteAsset(data: {
    assetType: SiteAssetType;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    influencer_id?: number; // For admin uploading on behalf of influencer
  }) {
    return this.request<PresignUploadResponse>('/influencer/uploads/site-asset/presign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async uploadSiteAsset(uploadUrl: string, file: File): Promise<{ success: boolean; objectKey: string }> {
    const response = await fetch(`${API_BASE}${uploadUrl.replace('/api', '')}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': file.type,
      },
      body: file,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  async completeSiteAssetUpload(data: {
    influencer_id?: number;
    assetType: SiteAssetType;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    objectKey: string;
    publicUrl: string;
  }) {
    return this.request<{ success: boolean; asset: InfluencerSiteAsset }>(
      '/influencer/uploads/site-asset/complete',
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  async deleteSiteAsset(assetId: number) {
    return this.request<{ success: boolean }>(`/influencer/uploads/site-asset/${assetId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================
  // VENDOR PORTAL ENDPOINTS
  // ============================================================

  async getVendorProfile() {
    return this.request<{ data: VendorProfile }>('/vendor/profile');
  }

  async updateVendorProfile(data: Partial<VendorProfile>) {
    return this.request<{ data: Vendor }>('/vendor/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateVendorLocale(locale: string) {
    return this.request<{ data: { preferred_locale: string } }>('/vendor/locale', {
      method: 'PATCH',
      body: JSON.stringify({ locale }),
    });
  }

  async vendorAcceptInvite(
    token: string,
    password: string,
    confirmPassword: string,
    turnstileToken: string
  ) {
    return this.request<{ success: boolean; user: User }>('/auth/vendor-accept-invite', {
      method: 'POST',
      body: JSON.stringify({ token, password, confirmPassword, turnstileToken }),
    });
  }

  // ── Admin vendor management ──

  async createVendorLogin(vendorId: number, email: string) {
    return this.request<{ user: { id: number; email: string }; inviteUrl: string; expiresAt: string }>(
      `/admin/vendors/${vendorId}/create-login`,
      { method: 'POST', body: JSON.stringify({ email }) }
    );
  }

  async sendVendorInvite(vendorId: number, email?: string) {
    return this.request<{ success: boolean; email_sent: boolean; email_error?: string; message: string; email: string; inviteUrl: string; expiresAt: string }>(
      `/admin/vendors/${vendorId}/send-invite`,
      { method: 'POST', body: JSON.stringify({ email }) }
    );
  }

  async updateVendorStatus(vendorId: number, status: VendorStatus) {
    return this.request<{ vendor: Vendor }>(
      `/admin/vendors/${vendorId}/status`,
      { method: 'PATCH', body: JSON.stringify({ status }) }
    );
  }

  // ── Admin vendor products ──

  async getAdminVendorProducts(filters?: {
    status?: string; vendor_id?: number; is_test?: boolean;
    search?: string; category?: string; limit?: number; offset?: number;
  }) {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.vendor_id) params.set('vendor_id', String(filters.vendor_id));
    if (filters?.is_test !== undefined) params.set('is_test', String(filters.is_test));
    if (filters?.search) params.set('search', filters.search);
    if (filters?.category) params.set('category', filters.category);
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));
    return this.request<{ data: any[]; total: number }>(`/admin/vendor-products?${params.toString()}`);
  }

  async getAdminVendorProduct(productId: string) {
    return this.request<{ data: any }>(`/admin/vendor-products/${productId}`);
  }

  async createAdminVendorProduct(data: Record<string, unknown>) {
    return this.request<{ data: any }>('/admin/vendor-products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminVendorProduct(productId: string, data: Record<string, unknown>) {
    return this.request<{ data: any }>(`/admin/vendor-products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async syncAdminVendorProductToShopify(productId: string) {
    return this.request<{ data: { action: string; shopify_product_id: string } }>(`/admin/vendor-products/${productId}/sync-shopify`, {
      method: 'POST',
    });
  }

  async adminImportCsvParse(vendorId: number, csv: string) {
    return this.request<{ data: { total: number; valid: number; errors: number; rows: any[]; vendor_id: number } }>('/admin/vendor-products/import-csv', {
      method: 'POST',
      body: JSON.stringify({ vendor_id: vendorId, csv }),
    });
  }

  async adminImportCsvConfirm(vendorId: number, rows: any[]) {
    return this.request<{ data: { created: number } }>('/admin/vendor-products/import-csv/confirm', {
      method: 'POST',
      body: JSON.stringify({ vendor_id: vendorId, rows }),
    });
  }

  async getAdminProductAuditLog(productId: string) {
    return this.request<{ data: any[] }>(`/admin/vendor-products/${productId}/audit-log`);
  }

  async approveVendorProduct(productId: string) {
    return this.request<{ data: any; shopify_error?: string }>(`/admin/vendor-products/${productId}/approve`, { method: 'PATCH' });
  }

  async rejectVendorProduct(productId: string, rejection_notes?: string) {
    return this.request<{ data: any }>(`/admin/vendor-products/${productId}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ rejection_notes }),
    });
  }

  async publishVendorProduct(productId: string, storefront_ids: string[]) {
    return this.request<{ data: any }>(`/admin/vendor-products/${productId}/publish`, {
      method: 'POST',
      body: JSON.stringify({ storefront_ids }),
    });
  }

  async unpublishVendorProduct(productId: string, storefront_ids: string[]) {
    return this.request<{ data: any }>(`/admin/vendor-products/${productId}/unpublish`, {
      method: 'POST',
      body: JSON.stringify({ storefront_ids }),
    });
  }

  async getVendorProductPublicationStatus(productId: string) {
    return this.request<{ data: any }>(`/admin/vendor-products/${productId}/publication-status`);
  }

  // ── Admin vendor applications ──

  async getVendorApplications(status?: string) {
    const params = status ? `?status=${status}` : '';
    return this.request<{ data: any[] }>(`/admin/vendor-applications${params}`);
  }

  async approveVendorApplication(id: number) {
    return this.request<{ data: any }>(`/admin/vendor-applications/${id}/approve`, { method: 'PATCH' });
  }

  async rejectVendorApplication(id: number, admin_notes?: string) {
    return this.request<{ data: any }>(`/admin/vendor-applications/${id}/reject`, {
      method: 'PATCH',
      body: JSON.stringify({ admin_notes }),
    });
  }

  // ── Admin audit log ──

  async getAuditLog(filters?: { entity_type?: string; action?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (filters?.entity_type) params.set('entity_type', filters.entity_type);
    if (filters?.action) params.set('action', filters.action);
    if (filters?.limit) params.set('limit', String(filters.limit));
    return this.request<{ data: any[] }>(`/admin/audit-log?${params.toString()}`);
  }

  // ── Admin payouts ──

  async getAdminPayouts(status?: string) {
    const params = status ? `?status=${status}` : '';
    return this.request<{ data: any[] }>(`/admin/payouts${params}`);
  }

  async approvePayout(id: number) {
    return this.request<{ data: any }>(`/admin/payouts/${id}/approve`, { method: 'PATCH' });
  }

  async processPayout(id: number) {
    return this.request<{ data: any }>(`/admin/payouts/${id}/process`, { method: 'PATCH' });
  }
}

// ============================================================
// TYPES
// ============================================================

export interface User {
  id: number;
  email: string;
  name?: string | null;
  role: 'admin' | 'influencer' | 'vendor';
  admin_level?: 'super_admin' | 'admin' | 'developer' | null;
  storefront_id?: number;
  vendor_id?: number;
}

export interface Influencer {
  id: number;
  first_name: string;
  last_name: string;
  stage_name: string;
  email: string | null;
  storefront_id: number;
  influencer_id: string;
  slug: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  profile_image_url?: string | null;
  primary_platform?: string | null;
  secondary_platforms?: string[] | null;
  social_handles?: Record<string, string> | null;
  follower_count?: number | null;
  engagement_rate?: number | null;
  industry?: string | null;
  bio?: string | null;
}

export interface InfluencerWithStatus extends Influencer {
  has_user: boolean;
  has_password: boolean;
  user_id: number | null;
  user_email: string | null;
  gmv?: number;
  total_orders?: number;
}

export interface InfluencerUser {
  id: number;
  email: string;
  has_password: boolean;
}

export interface InfluencerProfile {
  id: number;
  stage_name: string;
  first_name: string;
  last_name: string;
  storefront_id: number;
  influencer_id: string;
  slug: string;
  status: 'active' | 'inactive';
}

export interface CreateInfluencerData {
  first_name: string;
  last_name: string;
  stage_name: string;
  storefront_id?: number;   // auto-assigned by backend if omitted
  influencer_id?: string;   // auto-derived from stage_name if omitted
  slug?: string;            // auto-derived from stage_name if omitted
  email?: string;
  status?: 'active' | 'inactive';
}

export interface UpdateInfluencerData {
  first_name?: string;
  last_name?: string;
  stage_name?: string;
  email?: string;
  status?: 'active' | 'inactive';
}

export interface LoginResponse {
  user?: User;
  requiresVerification?: boolean;
  message?: string;
}

export interface SendInviteResponse {
  success: boolean;
  message: string;
  email: string;
  inviteUrl?: string; // Only in dev mode
  expiresAt: string;
}

export interface MeResponse {
  id: number;
  email: string;
  name?: string | null;
  role: 'admin' | 'influencer' | 'vendor';
  admin_level?: 'super_admin' | 'admin' | 'developer' | null;
  storefront_id?: number;
  vendor_id?: number;
  influencer?: {
    id: number;
    stage_name: string;
    first_name: string;
    last_name: string;
  };
  vendor?: {
    id: number;
    name: string;
    vendor_id: string;
    preferred_locale: string;
  };
}

export interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  role: 'admin';
  admin_level: 'super_admin' | 'admin' | 'developer' | null;
  created_at: string;
  has_password: boolean;
}

// Legacy storefront type (for backward compatibility)
export interface Storefront {
  id: number;
  storefront_id: number;
  influencer_id: string;
  display_name: string;
  storefront_slug: string;
  storefront_domain: string | null;
  creator_email: string;
  tier: string;
  status: 'active' | 'inactive';
  created_at: string;
}

export interface StorefrontWithStats extends Storefront {
  stats: {
    totalOrders: number;
    totalRevenue: number;
  };
}

export interface OrderFact {
  id: number;
  shopify_order_id: string;
  storefront_id: number | null;
  influencer_id: string | null;
  slug: string | null;
  attribution_status: 'verified' | 'unverified' | 'needs_review' | 'unattributed';
  attributed_source: 'note_attributes' | 'metafields' | 'none';
  created_at: string;
  total_price: number;
  currency: string;
  shipping_province: string | null;
  created_at_ingested: string;
  // Privacy-safe customer fields (no street, city, zip, phone, email)
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  order_country?: string | null;
  order_state?: string | null;
}

export interface OrderDetail {
  id: number;
  shopify_order_id: string;
  created_at: string;
  total_price: number;
  currency: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  order_country: string | null;
  order_state: string | null;
  shipping_province: string | null;
  attribution_status: 'verified' | 'unverified' | 'needs_review' | 'unattributed';
  attributed_source: 'note_attributes' | 'metafields' | 'none';
  storefront_id: number | null;
  influencer_id: string | null;
  slug: string | null;
}

export type MetricsRange = '7d' | '30d' | '90d' | 'all';

export interface MetricsSummary {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  currency: string;
  range: string;
}

export interface StateMetrics {
  province: string;
  orderCount: number;
  totalRevenue: number;
}

// ============================================================
// VENDOR & CATALOG TYPES
// ============================================================

export type ShippingCostType = 'flat' | 'per_item' | 'included' | 'quote';

export type VendorStatus = 'active' | 'inactive' | 'pending' | 'suspended' | 'archived';

export interface Vendor {
  id: number;
  vendor_id: string;
  vendor_slug: string | null;
  name: string;
  status: VendorStatus;
  ships_for_us: number;
  shipping_origin_country: string | null;
  shipping_origin_state: string | null;
  production_lead_time_days: number | null;
  shipping_lead_time_days: number | null;
  shipping_cost_type: ShippingCostType;
  shipping_cost_flat: number | null;
  contact_email: string | null;
  contact_phone: string | null;
  preferred_locale: string;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface VendorWithStatus extends Vendor {
  has_user: boolean;
  has_password: boolean;
  user_id: number | null;
  user_email: string | null;
}

export interface VendorProfile {
  id: number;
  vendor_id: string;
  name: string;
  status: VendorStatus;
  contact_email: string | null;
  contact_phone: string | null;
  preferred_locale: string;
  shipping_origin_country: string | null;
  shipping_origin_state: string | null;
  production_lead_time_days: number | null;
  shipping_lead_time_days: number | null;
  created_at: string;
}

export interface CreateVendorData {
  vendor_id: string;
  name: string;
  contact_email: string;
  status?: VendorStatus;
  ships_for_us?: number;
  shipping_origin_country?: string;
  shipping_origin_state?: string;
  production_lead_time_days?: number;
  shipping_lead_time_days?: number;
  shipping_cost_type?: ShippingCostType;
  shipping_cost_flat?: number;
  contact_phone?: string;
  preferred_locale?: string;
  notes?: string;
}

export interface UpdateVendorData {
  name?: string;
  status?: VendorStatus;
  ships_for_us?: number;
  shipping_origin_country?: string;
  shipping_origin_state?: string;
  production_lead_time_days?: number;
  shipping_lead_time_days?: number;
  shipping_cost_type?: ShippingCostType;
  shipping_cost_flat?: number;
  contact_email?: string;
  contact_phone?: string;
  preferred_locale?: string;
  notes?: string;
}

export interface CatalogProduct {
  id: number;
  vendor_catalog_product_id: string;
  vendor_id_fk: number;
  title: string;
  category: string;
  description: string | null;
  base_cost: number;
  min_retail: number;
  suggested_retail: number;
  customizable: number;
  printing_available: number;
  embroidery_available: number;
  ships_for_us: number | null;
  production_lead_time_days: number | null;
  shipping_cost_type: ShippingCostType | null;
  shipping_cost_flat: number | null;
  moq: number;
  tags: string | null;
  status: 'active' | 'inactive';
  created_at: string;
}

export interface CatalogProductWithVendor extends CatalogProduct {
  vendor_name: string;
}

export interface CatalogImage {
  id: number;
  vendor_catalog_product_id_fk: number;
  image_url: string;
  alt_text: string | null;
  sort_order: number;
  created_at: string;
}

export interface CatalogProductWithImages extends CatalogProductWithVendor {
  images: CatalogImage[];
}

export interface CatalogProductFilters {
  vendor_id_fk?: number;
  category?: string;
  status?: string;
  customizable?: number;
  printing_available?: number;
  embroidery_available?: number;
}

export interface CreateCatalogProductData {
  vendor_catalog_product_id: string;
  vendor_id_fk: number;
  title: string;
  category: string;
  description?: string;
  base_cost: number;
  min_retail: number;
  suggested_retail: number;
  customizable?: number;
  printing_available?: number;
  embroidery_available?: number;
  ships_for_us?: number;
  production_lead_time_days?: number;
  shipping_cost_type?: ShippingCostType;
  shipping_cost_flat?: number;
  moq?: number;
  tags?: string;
  status?: 'active' | 'inactive';
  images?: Array<{ image_url: string; alt_text?: string; sort_order?: number }>;
}

export interface UpdateCatalogProductData {
  title?: string;
  category?: string;
  description?: string;
  base_cost?: number;
  min_retail?: number;
  suggested_retail?: number;
  customizable?: number;
  printing_available?: number;
  embroidery_available?: number;
  ships_for_us?: number;
  production_lead_time_days?: number;
  shipping_cost_type?: ShippingCostType;
  shipping_cost_flat?: number;
  moq?: number;
  tags?: string;
  status?: 'active' | 'inactive';
}

// ============================================================
// PRODUCT REQUEST TYPES
// ============================================================

export type ProductRequestStatus =
  | 'submitted'
  | 'in_review'
  | 'design_in_progress'
  | 'waiting_on_vendor'
  | 'approved_to_create_shopify'
  | 'live'
  | 'rejected'
  | 'cancelled';

export interface ProductRequest {
  id: number;
  influencer_id_fk: number;
  vendor_catalog_product_id_fk: number;
  requested_customization: string | null;
  wants_designer: number;
  design_assets_url: string | null;
  requested_retail: number | null;
  notes: string | null;
  status: ProductRequestStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductRequestWithDetails extends ProductRequest {
  influencer_stage_name: string;
  product_title: string;
  vendor_catalog_product_id?: string;
}

export interface CreateProductRequestData {
  vendor_catalog_product_id_fk: number;
  requested_customization?: string;
  wants_designer?: number;
  design_assets_url?: string;
  requested_retail?: number;
  notes?: string;
}

// ============================================================
// SHOPIFY PRODUCT TYPES
// ============================================================

export type ShopifyProductLinkedBy = 'tags' | 'manual' | 'unlinked';

export interface ShopifyProduct {
  id: number;
  shopify_product_id: string;
  title: string | null;
  status: string | null;
  handle: string | null;
  tags: string | null;
  influencer_id_fk: number | null;
  vendor_catalog_product_id_fk: number | null;
  linked_by: ShopifyProductLinkedBy;
  created_at_shopify: string | null;
  updated_at_shopify: string | null;
  created_at: string;
}

// ============================================================
// ANALYTICS TYPES
// ============================================================

export interface TopShopifyProduct {
  shopify_product_id: string | null;
  title: string | null;
  handle: string | null;
  linked_by: string | null;
  influencer_id_fk: number | null;
  influencer_name: string | null;
  total_units: number;
  total_revenue: number;
  currency: string;
}

export interface TopCatalogProduct {
  vendor_catalog_product_id: number;
  vendor_catalog_product_string_id: string;
  title: string;
  vendor_name: string;
  total_units: number;
  total_revenue: number;
  currency: string;
}

export interface TopInfluencer {
  influencer_id: number;
  stage_name: string;
  first_name: string;
  last_name: string;
  storefront_id: number;
  total_orders: number;
  total_revenue: number;
  currency: string;
}

export interface ProductMetric {
  shopify_product_id: string | null;
  title: string | null;
  total_units: number;
  total_revenue: number;
  currency: string;
}

// ============================================================
// INFLUENCER CATALOG TYPES
// ============================================================

export interface InfluencerCatalogFilters {
  vendor_id_fk?: number;
  category?: string;
  customizable?: number;
  printing_available?: number;
  embroidery_available?: number;
}

export interface InfluencerCatalogProduct {
  id: number;
  vendor_catalog_product_id: string;
  vendor_id_fk: number;
  vendor_name: string;
  title: string;
  category: string;
  description: string | null;
  base_cost: number;
  min_retail: number;
  suggested_retail: number;
  customizable: number;
  printing_available: number;
  embroidery_available: number;
  moq: number;
  tags: string | null;
}

export interface InfluencerCatalogProductDetail extends InfluencerCatalogProduct {
  images: CatalogImage[];
}

// ============================================================
// INFLUENCER SITE TYPES
// ============================================================

export type SiteStatus = 'form_incomplete' | 'form_completed' | 'generating' | 'website_in_progress' | 'website_completed';
export type SiteAssetType = 'logo' | 'brand_photos' | 'product_photos' | 'brand_kit' | 'other';

export interface InfluencerSite {
  id: number;
  influencer_id_fk: number;
  status: SiteStatus;
  site_domain: string | null;
  // Per-influencer Shopify Hydrogen storefront credentials
  shopify_storefront_api_token: string | null;
  shopify_storefront_id: string | null;
  // Website generation pipeline
  github_repo_name: string | null;
  github_repo_url: string | null;
  worker_name: string | null;
  staging_url: string | null;
  production_url: string | null;
  generation_error: string | null;
  updated_at: string;
  created_at: string;
}

export interface InfluencerSiteIntake {
  id: number;
  influencer_id_fk: number;
  brand_vibe: string | null;
  color_scheme: string | null;
  font_preferences: string | null;
  inspiration_urls: string | null;
  social_links: string | null;
  about_copy: string | null;
  callouts: string | null;
  must_have_pages: string | null;
  notes: string | null;
  submitted_at: string | null;
  updated_at: string;
  created_at: string;
}

export interface InfluencerSiteAsset {
  id: number;
  influencer_id_fk: number;
  asset_type: SiteAssetType;
  file_name: string;
  content_type: string;
  size_bytes: number;
  object_key: string;
  public_url: string;
  created_at: string;
}

export interface SiteIntakeData {
  brand_vibe?: string;
  color_scheme?: string;
  font_preferences?: string;
  inspiration_urls?: string;
  social_links?: string;
  about_copy?: string;
  callouts?: string;
  must_have_pages?: string;
  notes?: string;
}

export interface SiteDataResponse {
  status: SiteStatus;
  site_domain: string | null;
  intake: InfluencerSiteIntake;
  assets: InfluencerSiteAsset[];
}

export interface PresignUploadResponse {
  uploadUrl: string;
  publicUrl: string;
  objectKey: string;
}

// Admin Websites Types
export interface AdminWebsiteListItem {
  influencer_db_id: number;
  influencer_id: string;
  stage_name: string;
  email: string | null;
  storefront_id: number;
  site_id: number | null;
  status: SiteStatus;
  site_domain: string | null;
  site_updated_at: string | null;
  site_created_at: string | null;
  intake_submitted_at: string | null;
}

export interface AdminWebsiteDetail {
  influencer: {
    id: number;
    influencer_id: string;
    stage_name: string;
    first_name: string;
    last_name: string;
    email: string | null;
    storefront_id: number;
  };
  site: InfluencerSite;
  intake: InfluencerSiteIntake;
  assets: InfluencerSiteAsset[];
}

// Website Editor Types
export interface FileEntry {
  path: string;
  type: 'file' | 'dir';
  sha: string;
}

export const api = new ApiClient();
